/**
 * "Add from library" — pick a product from the firm's library for a room.
 *
 * Custom markup rather than openModal(): picking from hundreds of products
 * needs live search, which a fixed form can't offer (CLAUDE.md's exception).
 * It borrows the modal's look.
 */

import { esc, fmtMoney } from '@/lib/dom';
import { searchLibrary } from '@/lib/material-library';
import { parseQuantity } from '@/lib/spec-table';
import type { MaterialLibraryRow } from '@/types/database';

export interface LibraryPickerOptions {
  items: MaterialLibraryRow[];
  roomName: string;
  /** Resolves when the material has been added; rejects to show the error. */
  onPick: (item: MaterialLibraryRow, quantity: number) => Promise<void>;
}

export function openLibraryPicker(opts: LibraryPickerOptions): HTMLElement {
  const overlay = document.createElement('div');
  overlay.className = 'modal';
  overlay.innerHTML = `
    <div class="modal-card library-picker" role="dialog" aria-label="Add from library">
      <h2>Add to ${esc(opts.roomName)} from the library</h2>
      <p class="muted small">It’s copied into this room — adjust quantity, price or status here without touching the library.</p>
      <div class="library-picker-bar">
        <input type="search" data-picker-search placeholder="Search by name, vendor or SKU" />
        <label class="library-picker-qty">Qty <input type="number" min="0" step="any" value="1" data-picker-qty /></label>
      </div>
      <ul class="library-picker-list" data-picker-list></ul>
      <div class="modal-error" hidden></div>
      <div class="modal-actions">
        <span style="flex:1"></span>
        <button type="button" class="btn-link" data-action="cancel">Close</button>
      </div>
    </div>`;

  const list = overlay.querySelector<HTMLElement>('[data-picker-list]')!;
  const search = overlay.querySelector<HTMLInputElement>('[data-picker-search]')!;
  const qty = overlay.querySelector<HTMLInputElement>('[data-picker-qty]')!;
  const errBox = overlay.querySelector<HTMLElement>('.modal-error')!;
  const byId = new Map(opts.items.map((i) => [i.id, i] as const));

  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };

  const draw = (): void => {
    const shown = searchLibrary(opts.items, search.value).slice(0, 50);
    list.innerHTML = shown.map((i) => `
      <li>
        <button type="button" class="library-picker-item" data-pick="${esc(i.id)}">
          <span><strong>${esc(i.item)}</strong>
            <span class="muted small">${esc([i.vendor, i.sku].filter(Boolean).join(' · '))}</span></span>
          <span class="muted">${i.unit_cost_cents == null ? '' : `${fmtMoney(i.unit_cost_cents)}${i.unit ? ` / ${esc(i.unit)}` : ''}`}</span>
        </button>
      </li>`).join('') || `<li class="muted">Nothing matches.</li>`;
    list.querySelectorAll<HTMLButtonElement>('[data-pick]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const item = byId.get(btn.dataset.pick!)!;
        const q = parseQuantity(qty.value) ?? 1;
        list.querySelectorAll<HTMLButtonElement>('[data-pick]').forEach((b) => { b.disabled = true; });
        errBox.hidden = true;
        try {
          await opts.onPick(item, q > 0 ? q : 1);
          close();
        } catch (err) {
          errBox.textContent = err instanceof Error ? err.message : 'Could not add it';
          errBox.hidden = false;
          list.querySelectorAll<HTMLButtonElement>('[data-pick]').forEach((b) => { b.disabled = false; });
        }
      });
    });
  };

  search.addEventListener('input', draw);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelectorAll('[data-action="cancel"]').forEach((el) => el.addEventListener('click', close));
  draw();
  document.body.append(overlay);
  document.addEventListener('keydown', onKey);
  search.focus();
  return overlay;
}
